import { ChevronDown } from 'lucide-react-native';
import { useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { COMMON_ROLES } from '@/lib/contacts';

export interface ContactFormState {
  name: string;
  role: string;
  company: string;
  phone: string;
  email: string;
  website: string;
  notes: string;
}

export function emptyContactForm(): ContactFormState {
  return {
    name: '',
    role: '',
    company: '',
    phone: '',
    email: '',
    website: '',
    notes: '',
  };
}

interface Props {
  value: ContactFormState;
  onChange: (v: ContactFormState) => void;
}

export function ContactForm({ value, onChange }: Props) {
  const [roleOpen, setRoleOpen] = useState(false);

  const set = (key: keyof ContactFormState, v: string) => {
    onChange({ ...value, [key]: v });
  };

  return (
    <View>
      <Field label="Name" value={value.name} onChangeText={(v) => set('name', v)} placeholder="e.g. Mike at Acme HVAC" />

      <Text style={styles.fieldLabel}>ROLE</Text>
      <Pressable
        onPress={() => setRoleOpen(true)}
        style={({ pressed }) => [styles.roleField, pressed && { opacity: 0.94 }]}>
        <Text style={[styles.roleText, !value.role && styles.rolePlaceholder]}>
          {value.role || 'Tap to pick a role'}
        </Text>
        <ChevronDown size={16} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>

      <Field label="Company" value={value.company} onChangeText={(v) => set('company', v)} placeholder="Optional" />
      <Field label="Phone" value={value.phone} onChangeText={(v) => set('phone', v)} placeholder="(555) 123-4567" inputMode="numeric" />
      <Field label="Email" value={value.email} onChangeText={(v) => set('email', v)} placeholder="mike@acme.com" inputMode="text" />
      <Field label="Website" value={value.website} onChangeText={(v) => set('website', v)} placeholder="acmehvac.com" />

      <Text style={styles.fieldLabel}>NOTES</Text>
      <TextInput
        value={value.notes}
        onChangeText={(v) => set('notes', v)}
        placeholder="Service area, rates, what they did last time"
        placeholderTextColor={Palette.inkFaint}
        multiline
        style={[styles.fieldInput, { height: 100, textAlignVertical: 'top' }]}
      />

      <Modal
        animationType="fade"
        transparent
        visible={roleOpen}
        onRequestClose={() => setRoleOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoleOpen(false)}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Pick a role</Text>
            <ScrollView style={{ maxHeight: 320 }}>
              {COMMON_ROLES.map((r) => (
                <Pressable
                  key={r}
                  onPress={() => {
                    set('role', r);
                    setRoleOpen(false);
                  }}
                  style={({ pressed }) => [
                    styles.roleOption,
                    pressed && { backgroundColor: '#EEF3EE' },
                    value.role === r && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.roleOptionText}>{r}</Text>
                </Pressable>
              ))}
              <Pressable
                onPress={() => setRoleOpen(false)}
                style={({ pressed }) => [styles.roleOption, pressed && { backgroundColor: '#EEF3EE' }]}>
                <Text style={[styles.roleOptionText, { color: Palette.inkFaint }]}>
                  Or type a custom role above
                </Text>
              </Pressable>
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        style={styles.fieldInput}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  fieldLabel: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
    marginTop: Spacing.three,
  },
  fieldInput: {
    marginTop: 5,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  roleField: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 5,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  roleText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  rolePlaceholder: {
    color: Palette.inkFaint,
    fontFamily: Fonts.sansMedium,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 360,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    marginBottom: Spacing.three,
  },
  roleOption: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
  },
  roleOptionText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
});
